import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import AppAnalytics from '../analytics/AppAnalytics';

@customElement('tools-tabs')
export class ToolsTabs extends LitElement {
  @state()
  private activeTab: 'generate' | 'train' = 'generate';

  static styles = css`
    :host {
      display: block;
      max-width: 100%;
    }

    .tabs-container {
      background: linear-gradient(135deg,
        rgba(0, 212, 255, 0.1) 0%,
        rgba(168, 85, 247, 0.1) 50%,
        rgba(236, 72, 153, 0.1) 100%);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(0, 212, 255, 0.3);
      border-radius: 1rem;
      overflow: hidden;
    }

    .tabs-header {
      display: flex;
      border-bottom: 1px solid rgba(0, 212, 255, 0.2);
    }

    .tab-button {
      flex: 1;
      background: none;
      border: none;
      padding: 1.5rem 2rem;
      font-family: 'JetBrains Mono', monospace;
      font-weight: bold;
      font-size: 1rem;
      cursor: pointer;
      transition: all 0.2s;
      position: relative;
      color: rgba(226, 232, 240, 0.6);
    }

    .tab-button:hover {
      color: #00d4ff;
      background: rgba(0, 212, 255, 0.05);
    }

    .tab-button.active {
      color: #00d4ff;
      background: rgba(0, 212, 255, 0.1);
    }

    .tab-button.active::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: #00d4ff;
      box-shadow: 0 0 10px #00d4ff;
    }

    .tab-icon {
      margin-right: 0.5rem;
      font-size: 1.25rem;
    }

    .tab-content {
      padding: 2rem;
    }

    .tab-panel {
      display: none;
    }

    .tab-panel.active {
      display: block;
    }

    .tool-header {
      text-align: center;
      margin-bottom: 2rem;
    }

    .tool-title {
      font-size: 2rem;
      font-weight: bold;
      font-family: 'JetBrains Mono', monospace;
      color: #00d4ff;
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
      margin-bottom: 0.75rem;
    }

    .tool-subtitle {
      color: rgba(226, 232, 240, 0.7);
      font-family: 'JetBrains Mono', monospace;
      font-size: 1rem;
      line-height: 1.5;
    }

    .form-wrapper {
      max-width: 42rem;
      margin: 0 auto;
    }

    .cyber-green { color: #00ff88; }
    .cyber-yellow { color: #ffd700; }
    .cyber-purple { color: #a855f7; }
    .cyber-pink { color: #ec4899; }

    @media (max-width: 768px) {
      .tab-button {
        padding: 1rem;
        font-size: 0.875rem;
      }

      .tab-content {
        padding: 1.5rem;
      }

      .tool-title {
        font-size: 1.5rem;
      }
    }
  `;

  private switchTab(tab: 'generate' | 'train') {
    this.activeTab = tab;

    this.requestUpdate()
    // Track tab switching
    AppAnalytics.getPlausible()?.trackEvent('tab_switched', {
      props: {
        page: 'homepage',
        tab: tab,
        location: 'tools_tabs'
      }
    });
  }

  render() {
    return html`
      <div class="tabs-container">
        <div class="tabs-header">
          <button
            class="tab-button ${this.activeTab === 'generate' ? 'active' : ''}"
            @click=${() => this.switchTab('generate')}>
            <span class="tab-icon">🎨</span>
            GENERATE_IMAGES
          </button>
          <button
            class="tab-button ${this.activeTab === 'train' ? 'active' : ''}"
            @click=${() => this.switchTab('train')}>
            <span class="tab-icon">🚀</span>
            TRAIN_LORA
          </button>
        </div>

        <div class="tab-content">
          <!-- Generate Images Tab -->
          <div class="tab-panel ${this.activeTab === 'generate' ? 'active' : ''}">
            <div class="tool-header">
              <h2 class="tool-title">
                GENERATE_<span class="cyber-pink">IMAGES</span>
              </h2>
              <p class="tool-subtitle">
                <span class="cyber-green">[CREATE]</span> stunning images using your custom LoRA models
              </p>
            </div>
            <div class="form-wrapper">
              <generate-images-form></generate-images-form>
            </div>
          </div>

          <!-- Train LoRA Tab -->
          <div class="tab-panel ${this.activeTab === 'train' ? 'active' : ''}">
            <div class="tool-header">
              <h2 class="tool-title">
                TRAIN_<span class="cyber-green">LORA</span>_MODEL
              </h2>
              <p class="tool-subtitle">
                <span class="cyber-yellow">[UPLOAD]</span> 10-20 high-quality images and we'll create a custom LoRA model for you
              </p>
            </div>
            <div class="form-wrapper">
              <train-lora-form></train-lora-form>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tools-tabs': ToolsTabs;
  }
}
